// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  UNIT_OF_MEASURE_OPTIONS,
  UOM_DISPLAY_MAP,
  UnitOfMeasure,
} from '@/constants/unit-of-measure';
import { Autocomplete, TextField } from '@mui/material';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

interface Props<TFieldValues extends FieldValues> {
  /**
   * Name of the form field.
   */
  name: Path<TFieldValues>;

  /**
   * Form control from react-hook-form.
   */
  control: Control<TFieldValues>;

  /**
   * Label for the select input.
   * @default "Unit of measure"
   */
  label?: string;
}

/**
 * A form autocomplete component for unit of measure options.
 * Provides a grouped typeahead dropdown with comprehensive unit of measure options.
 */
export function UnitOfMeasureSelect<TFieldValues extends FieldValues>(
  props: Props<TFieldValues>
) {
  return (
    <Controller
      name={props.name}
      control={props.control}
      render={({ field, fieldState }) => (
        <Autocomplete
          options={[...UNIT_OF_MEASURE_OPTIONS]}
          groupBy={(option) => option.group}
          getOptionLabel={(option) =>
            typeof option === 'string'
              ? (UOM_DISPLAY_MAP[option as UnitOfMeasure] ?? option)
              : option.label
          }
          value={
            UNIT_OF_MEASURE_OPTIONS.find((opt) => opt.value === field.value) ??
            null
          }
          onChange={(_, newValue) => {
            field.onChange(newValue?.value ?? '');
          }}
          onBlur={field.onBlur}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              label={props.label ?? 'Unit of measure'}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            />
          )}
        />
      )}
    />
  );
}
